import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Gift,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  DollarSign,
  Users,
  TrendingUp,
} from "lucide-react";

const PartnerLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          target_role: "referral_partner"  // Specify we want to log into partner account
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Invalid email or password");
      }

      // Verify we got a partner account
      if (data.user?.role !== "referral_partner") {
        throw new Error("This login is for referral partners only. Please use the client portal or admin login.");
      }

      login(data.user, data.token);
      toast.success("Welcome back, Partner!");
      navigate("/partner");
    } catch (error) {
      toast.error(error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Partner Login | RNB Consulting Group</title>
        <meta
          name="description"
          content="Log in to your referral partner dashboard to track referrals, earnings, and request payouts."
        />
      </Helmet>

      <div className="min-h-screen flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-rnb-navy via-rnb-blue to-rnb-teal relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />
          
          <div className="relative z-10 flex flex-col justify-center px-12 text-white">
            <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Website
            </Link>

            <div className="mb-8">
              <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-6">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <h1 className="font-heading text-4xl font-bold mb-4">
                Partner Portal
              </h1>
              <p className="text-xl text-white/80 mb-8">
                Track your referrals, monitor earnings, and grow your income.
              </p>
            </div>

            {/* Stats Preview */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                <div className="p-3 bg-white/10 rounded-lg">
                  <DollarSign className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">$150</p>
                  <p className="text-sm text-white/70">Per Confirmed Referral</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                <div className="p-3 bg-white/10 rounded-lg">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">Unlimited</p>
                  <p className="text-sm text-white/70">Earning Potential</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                <div className="p-3 bg-white/10 rounded-lg">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">7-10 Days</p>
                  <p className="text-sm text-white/70">Payout Processing</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-rnb-light">
          <div className="w-full max-w-md">
            {/* Mobile Back Link */}
            <Link
              to="/"
              className="lg:hidden flex items-center gap-2 text-rnb-gray hover:text-rnb-navy mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Website
            </Link>

            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rnb-navy to-rnb-blue flex items-center justify-center">
                <Gift className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-rnb-navy">Partner Portal</h2>
                <p className="text-sm text-rnb-gray">RNB Consulting Group</p>
              </div>
            </div>

            <Card className="card-modern">
              <CardHeader className="text-center">
                <CardTitle className="font-heading text-2xl text-rnb-navy">
                  Welcome Back, Partner!
                </CardTitle>
                <CardDescription>
                  Log in to access your referral dashboard
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-rnb-gray" />
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      data-testid="partner-login-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-rnb-gray" />
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        required
                        data-testid="partner-login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-rnb-gray hover:text-rnb-navy"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-rnb-blue hover:text-rnb-navy transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary py-6"
                    disabled={loading}
                    data-testid="partner-login-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In to Dashboard"
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t text-center">
                  <p className="text-sm text-rnb-gray mb-3">
                    Not a partner yet?
                  </p>
                  <Link to="/referral-program/apply">
                    <Button variant="outline" className="w-full">
                      Apply to Join the Program
                    </Button>
                  </Link>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-center text-sm text-blue-800">
                    <span className="font-medium">Client?</span>{" "}
                    <Link to="/login" className="text-blue-700 hover:underline font-semibold">
                      Log in to your Client Portal →
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default PartnerLogin;
